import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";

const emptyProduct = {
  name: "", description: "", price: 0, category: "Batteries",
  stock: 0, image_url: "", brand: "", featured: false,
};

export default function AdminProductsTab({ products, setProducts, loadAll }) {
  const [editing, setEditing] = useState(null); // product being edited/created
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing({ isNew: true }); setForm(emptyProduct); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), stock: Number(form.stock) };
      if (editing?.isNew) {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      setEditing(null);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      setProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button data-testid="new-product-btn" onClick={openCreate} className="rounded-full bg-brand hover:bg-brand-hover text-white px-5">
          <Plus className="w-4 h-4 mr-1.5" /> New product
        </Button>
      </div>
      <div className="bg-white rounded-3xl border border-zinc-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg product-image-bg overflow-hidden flex-shrink-0">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="font-medium text-zinc-900">{p.name}</div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-zinc-600">{p.category}</TableCell>
                <TableCell className="font-semibold">{formatIDR(p.price)}</TableCell>
                <TableCell>{p.stock}</TableCell>
                <TableCell>{p.featured ? <span className="text-xs font-bold text-brand">YES</span> : <span className="text-xs text-zinc-400">—</span>}</TableCell>
                <TableCell className="text-right">
                  <button data-testid={`edit-product-${p.id}`} onClick={() => openEdit(p)} className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-zinc-100 mr-1">
                    <Pencil className="w-4 h-4 text-zinc-600" />
                  </button>
                  <button data-testid={`delete-product-${p.id}`} onClick={() => del(p.id)} className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-brand" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">{editing?.isNew ? "New product" : "Edit product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="pf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (Rp)</Label>
                <Input data-testid="pf-price" type="number" step="1000" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
              </div>
              <div>
                <Label>Stock</Label>
                <Input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="pf-cat" className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Repair Services">Repair Services</SelectItem>
                    <SelectItem value="Batteries">Batteries</SelectItem>
                    <SelectItem value="Parts">Parts</SelectItem>
                    <SelectItem value="Accessories">Accessories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Brand</Label>
                <Input data-testid="pf-brand" value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
              </div>
            </div>
            <div>
              <Label>Image URL</Label>
              <Input data-testid="pf-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="pf-featured" type="checkbox" checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#E31837]" />
              Feature on homepage
            </label>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Cancel</Button>
              <Button data-testid="pf-save" type="submit" disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-white">
                {saving ? "Saving…" : editing?.isNew ? "Create product" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
